import axiosAPI from 'axios';
import * as loader from "../components/loader";
import { clearStoredAuth, getStoredAuth } from "./authStorage";
import { takeLoginPhoto } from "./loginPhoto";
import * as journal from "./journal";

const axios = axiosAPI.create({
    baseURL: process.env.REACT_APP_API_URL
})

axios.interceptors.request.use(async request => {
    const systemId = localStorage.getItem('BOR_SYSTEM_ID');
    if (systemId) {
        request.headers['examSoftwareSystemId'] = systemId;
    }
    // The candidate's journal rides on the timer, answer saves and submit (src/utils/journal.js)
    if (journal.carries(request.method, request.url) && request.data && typeof request.data === "object") {
        const pending = await journal.take();
        if (pending) {
            request.data = { ...request.data, journal: pending.journal };
            request.journalSid = pending.sid;
        }
    }

    return request;
});

// The server says up to which journal entry it kept (it does even when it refuses the request itself)
const ackJournal = (config, headers) => {
    const kept = parseInt(headers?.["x-journal-ack"], 10);
    if (config?.journalSid && kept > 0) journal.ack(config.journalSid, kept);
};

// Several requests can fail at once when a session ends; handle it once
let sessionEnded = false;

// 905 / 907: the server refused this login because it now comes from another machine. Before the login is ended,
// a picture of whoever is at this one goes to the server (kept with the login photos as "Logged out"), sent
// with the login's token. Never fails: the login is ended either way.
export const OTHER_MACHINE = [905, 907];
export const photographLogout = async (code, message) => {
    try {
        const token = getStoredAuth()?.token;
        if (!token) return;
        const photo = await takeLoginPhoto();
        await axios.post("/auth/logout-photo", { code, message, ...photo }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
        // ending the login matters more than the picture
    }
};

axios.interceptors.response.use(function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    ackJournal(response.config, response.headers);
    return response;
}, function (error) {
    console.log(error?.response);
    ackJournal(error?.config, error?.response?.headers);
    // Into the journal: which request failed and why (no response at all: status 0)
    const failedPath = String(error?.config?.url || "").split("?")[0];
    journal.log("REQUEST_FAILED", {
        v: error?.response?.status || 0,
        d: [String(error?.config?.method || "").toUpperCase(), failedPath, error?.response?.data?.errorCode, error?.response?.data?.message || error?.message]
            .filter(Boolean)
            .join(" "),
    });
    // 901: session ended on the server (logged in again elsewhere, expired or revoked). 905/907: this login
    // belongs to another machine than the one the request came from (the system ID is read again on every page
    // load and every minute), so it cannot go on here either. 902 (machine not identified, e.g. the registration
    // app stopped) does not end the login: it works again once the app is back. 912: the page asked for an exam that
    // is not the logged-in candidate's (it held another person's), so it starts again from the login page.
    if ([901, 905, 907, 912].includes(error?.response?.data?.errorCode) && window.location.pathname !== "/") {
        if (sessionEnded) return Promise.reject(error);
        sessionEnded = true;
        const { errorCode, message } = error.response.data;
        loader.notification(false, message)
        // Cleared, then redirected after the message has been seen, unless a new login has been stored since.
        // Another machine: the picture is taken first, while the login (and its token) still exists.
        const end = () => {
            clearStoredAuth();
            setTimeout(() => {
                if (!getStoredAuth()) window.location.href = "/"
            }, 1500);
        };
        if (OTHER_MACHINE.includes(errorCode)) photographLogout(errorCode, message).finally(end);
        else end();
        return Promise.reject(error);
    }
    else if (error?.response?.data?.errorCode === 900) {
        loader.notification(false, "The exam is already submitted. We are redirecting you to login page")
        setTimeout(() => {
            localStorage.clear();
            clearStoredAuth();
            window.location.href = "/"
        }, 1500);
        // return Promise.reject(error);
    }
    else
        return Promise.reject(error);
});


export { axios };